import React, { useState } from 'react';
import OTP from '../components/OTP';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api/api';
import AdminLogin from '../components/AdminPassword';

const Login = () => {
    const location = useLocation();
    // Pre-fill email when arriving from the registration page.
    const [email, setEmail] = useState(location.state?.email || '');
    const [isEmailSent, setIsEmailSent] = useState(false);
    const navigate = useNavigate();
    const [authError, setAuthError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);

    const handleSendOTP = async () => {
        setAuthError('');
        try {
            // First verify the email
            const emailResponse = await api.post('/users/verifyEmail', { email });

            if (emailResponse.status === 201) {
                // This is an admin email
                setIsAdmin(true);
                setIsEmailSent(true);
                return;
            }

            // For non-admin users, the SERVER generates and stores the OTP.
            const otpResponse = await api.post('/users/sendotp', { email });

            setSuccessMessage(otpResponse.data?.message || 'OTP sent successfully');
            setTimeout(() => {
                setIsEmailSent(true);
            }, 1500);
        } catch (error) {
            console.error(error);
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        setAuthError('Email not found');
                        break;
                    case 429:
                        setAuthError('Too many attempts. Please try again later.');
                        break;
                    default:
                        setAuthError(error.response.data?.message || 'Failed to send OTP. Please try again.');
                }
            } else {
                setAuthError('Network error. Please try again.');
            }
        }
    };

    return !isEmailSent ? (
        <div className="min-h-screen flex items-center justify-center bg-[#eff1f3] px-4">
            <div className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Header */}
                <div className="px-8 pt-6 pb-4 text-center">
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                        Welcome back
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Enter your email to receive a one-time password
                    </p>
                </div>
                
                {/* Content */}
                <div className="px-8 pb-6 space-y-4">
                    <div className="space-y-2">
                        <label 
                            htmlFor="email" 
                            className="block text-sm font-medium text-gray-700"
                        >
                            Email address
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg 
                                    className="h-5 w-5 text-[#a9b9e3]" 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="currentColor"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
                                    />
                                </svg>
                            </div>
                            <input
                                id="email"
                                type="email"
                                placeholder="name@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 border border-[#a9b9e3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6f48e5] focus:border-[#6f48e5] transition-colors duration-200"
                            />
                        </div>
                    </div>

                    {authError && (
                        <div className="rounded-md bg-red-50 p-4 border border-red-200">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-red-800">{authError}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {successMessage && (
                        <div className="rounded-md bg-green-50 p-4 border border-green-200">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-10.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414L9 13.414l5.707-5.707z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-green-800">{successMessage}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleSendOTP}
                        disabled={!email}
                        className="w-full py-2 px-4 bg-[#6f48e5] hover:bg-[#5a3ac4] text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:bg-[#a9b9e3] disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6f48e5]"
                    >
                        Send OTP
                    </button>
                </div>
                
                {/* Footer */}
                <div className="px-8 pb-6 text-center">
                    <p className="text-sm text-gray-600">
                        Don't have an account?{' '}
                        <button
                            onClick={() => navigate('/signUp')}
                            className="text-[#6f48e5] hover:text-[#5a3ac4] font-medium focus:outline-none"
                        >
                            Sign up
                        </button>
                    </p>
                </div>
            </div>
        </div>
    ) : (
       isAdmin ? <AdminLogin /> : <OTP email={email} />
    );
};

export default Login;